<script setup lang='ts'>
import { CalendarDays, Plus, Search } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import useResponsive from '@/composables/useResponsive';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { useProductsStore } from '@/stores/useProductsStore';
import { goToRoute, useFuseLandingSearch } from '@/utils/utils';

import TheCalendar from './TheCalendar.vue';
import TheMobileFilters from './TheMobileFilters.vue';
import TheThemesDropdown from './TheThemesDropdown.vue';

const { t } = useI18n({ useScope: 'global' });
const { isMobile } = useResponsive();

const metricsStore = useMetricsStore();
const { themes } = storeToRefs(metricsStore);

const productsStore = useProductsStore();

const filtersStore = useFiltersStore();
const { filters, datesAsStrings } = storeToRefs(filtersStore);

const globalConfigurationStore = useGlobalConfigurationStore();
const { routingOptions, globalConfiguration, currentLocale } = storeToRefs(globalConfigurationStore);

const openThemesPopover = ref(false);
const openCalendarPopover = ref(false);
const themesPopover = ref();
const calendarPopover = ref();

const {
  matchingResults: matchingRoutes,
  debouncedSearch: debouncedHandleLandingSuggestions,
} = useFuseLandingSearch(() => routingOptions.value ?? []);

const hasCalendar = computed(() => globalConfiguration.value?.options?.hasCalendar ?? true);

const shouldDisplayThemesDropdown = computed(() => (globalConfiguration.value?.diffusion?.hasThemes ?? true) && (globalConfiguration.value?.options?.hasThemes ?? true));

const currentSearch = computed(() => filters.value.sorts.find(it => it.type === 'search')?.query ?? '');
const activeTheme = computed(() => filters.value.selections.find(it => it.filterProperties.type === 'taxonomyThemes')?.selection?.[0]) ?? undefined;
const activeThemeLabel = computed(() => themes.value.find(it => it.id === activeTheme.value)?.translatedName.find((it: any) => it.lang === currentLocale.value)?.value ?? t('search.themes'));

const input = ref(currentSearch.value);
const isInputFocused = ref(false);

async function handleSearchInput(event: any = null) {
  if (event && event.code === 'Enter') {
    (event.target as HTMLInputElement).blur();
  }
  filtersStore.addSearchingFilter(input.value);
  const defaultRoute = routingOptions.value?.find(it => it.taxonomies?.length === 0);
  if (defaultRoute && window.location.href !== defaultRoute.url) {
    window.location.href = defaultRoute.url;
  }
  else {
    const baseFilters = { ...filters.value };
    baseFilters.sorts = baseFilters.sorts.filter((it: any) => it.type !== 'search');
    await metricsStore.getBaseMetrics(baseFilters);
    await productsStore.fetchProductsList(filters.value);
  }
}

function toggleThemesPopover() {
  if (openCalendarPopover.value === true) {
    openCalendarPopover.value = false;
    window.removeEventListener('click', handleCalendarOutsideClick);
  }
  openThemesPopover.value = !openThemesPopover.value;
  setTimeout(() => {
    openThemesPopover.value === true ? window.addEventListener('click', handleThemesOutsideClick) : window.removeEventListener('click', handleThemesOutsideClick);
    openThemesPopover.value === true ? window.addEventListener('keydown', handleThemesOutsideClick) : window.removeEventListener('keydown', handleThemesOutsideClick);
  }, 100);
}

function toggleCalendarPopover() {
  if (openThemesPopover.value === true) {
    openThemesPopover.value = false;
    window.removeEventListener('click', handleThemesOutsideClick);
  }
  openCalendarPopover.value = !openCalendarPopover.value;
  setTimeout(() => {
    openCalendarPopover.value === true ? window.addEventListener('click', handleCalendarOutsideClick) : window.removeEventListener('click', handleCalendarOutsideClick);
    openCalendarPopover.value === true ? window.addEventListener('keydown', handleCalendarOutsideClick) : window.removeEventListener('keydown', handleCalendarOutsideClick);
  }, 100);
}

function handleThemesOutsideClick(event: Event) {
  // @ts-expect-error typeof event
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown')
    return;
  const target = event.target as HTMLElement;
  // @ts-expect-error typeof event
  if (!themesPopover.value || !themesPopover.value.contains(target) || event.key === 'Escape') {
    toggleThemesPopover();
  }
};

function handleCalendarOutsideClick(event: Event) {
  const target = event.target as HTMLElement;
  const calendar = document.querySelector('yb-search');
  if (calendar && (target !== calendar)) {
    toggleCalendarPopover();
  }
  // @ts-expect-error typeof event
  if (event.key === 'Escape') {
    toggleCalendarPopover();
  }
}

function deleteTheme() {
  filters.value.metrics = filters.value.metrics.filter(it => it.type !== 'crossedTaxonomyCount');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties?.type !== 'taxonomyThemes');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties?.type !== 'taxonomyTypes');

  filtersStore.saveToStorage(filters.value);
  const defaultRoute = routingOptions.value?.find(it => it.taxonomies?.length === 0);
  if (defaultRoute && window.location.href !== defaultRoute.url) {
    window.location.href = defaultRoute.url;
  }
  else {
    productsStore.fetchProductsList(filters.value);
  }
}

function deleteDates() {
  const definedDates = filters.value.filters.findIndex(it => it.filterProperties.type === 'date_range');
  if (definedDates > -1) {
    filters.value.filters.splice(definedDates, 1);
  }
  datesAsStrings.value = null;
  filtersStore.saveToStorage(filters.value);
  productsStore.fetchProductsList(filters.value);
}

type RangeParams = {
  start: number;
  end: number;
};

function getValidRangeParams(params: URLSearchParams): RangeParams | undefined {
  let startStr = params.get('start');
  let endStr = params.get('end');

  if (!startStr && !endStr)
    return undefined;

  if (startStr && !endStr)
    endStr = startStr;
  if (!startStr && endStr)
    startStr = endStr;

  const toTimestamp = (val: string): number | null => {
    if (/^\d+$/.test(val)) {
      return Number.parseInt(val, 10);
    }

    const parsed = Date.parse(val);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const start = toTimestamp(startStr!);
  const end = toTimestamp(endStr!);

  if (start === null || end === null) {
    return undefined;
  }

  return { start, end };
}

function resetThemeSelection() {
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties.type !== 'taxonomyThemes');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties.type !== 'taxonomyTypes');
  filters.value.metrics = filters.value.metrics.filter(it => it.type !== 'crossedTaxonomyCount');
  filtersStore.saveToStorage(filters.value);
}

watch(isInputFocused, (focus) => {
  if (focus)
    toggleCalendarPopover();
});

onMounted(async () => {
  // Enable state on pages with mode-details and no list component
  const hasListComponent = document.querySelector('yb-list');
  if (!hasListComponent) {
    filtersStore.loadFromStorage();
    await productsStore.fetchProductsList(filters.value);
    await metricsStore.getBaseMetrics(filters.value);
  }

  const savedDates = filters.value.filters.find(it => it.filterProperties.type === 'date_range')?.selection;
  if (savedDates)
    filtersStore.setDatesAsStrings({ start: savedDates.startDate, end: savedDates.endDate });

  const params = new URLSearchParams(window.location.search);
  const rangeParams = getValidRangeParams(params);

  if (rangeParams)
    filtersStore.addDatesFilter(rangeParams);

  const isHomeURL = globalConfiguration.value?.homeUrl === window.location.href;
  if (isHomeURL)
    resetThemeSelection();
});
</script>

<template>
  <div v-if="!isMobile" class="search-container yb-preset">
    <section :aria-label="t('search.label')">
      <div class="search-filter">
        <div class="filter-wrapper" tabindex="0">
          <Search :size="18" color="var(--yb-color-black)" aria-hidden="true" />
          <input
            v-model="input"
            type="search"
            class="search_query"
            :placeholder="t('search.placeholder')"
            :aria-label="t('search.placeholder')"
            @input="debouncedHandleLandingSuggestions(input, isInputFocused)"
            @keydown.enter="(v) => handleSearchInput(v)"
            @focus="isInputFocused = true;"
            @blur="isInputFocused = false;"
          >
        </div>
      </div>
      <div v-if="shouldDisplayThemesDropdown" class="search-filter">
        <div class="search-buttons">
          <button
            type="button" class="filter-wrapper" :class="{ opened: openThemesPopover }" tabindex="0"
            :aria-pressed="openThemesPopover" :aria-label="t('search.togglethemes')" @click="toggleThemesPopover"
          >
            <p :class="{ active: activeTheme }">
              {{ activeThemeLabel }}
            </p>
          </button>
          <button
            v-if="activeTheme" type="button" class="delete-button" aria-label="Supprimer le theme selectionné"
            @click="deleteTheme"
          >
            <Plus :size="18" color="var(--yb-color-black)" style="rotate:45deg;" />
          </button>
        </div>
        <div v-if="openThemesPopover" ref="themesPopover" class="popover">
          <TheThemesDropdown />
        </div>
      </div>
      <div v-if="hasCalendar" class="search-filter calendar">
        <div class="search-buttons">
          <button
            type="button" class="filter-wrapper" :class="{ opened: openCalendarPopover }" tabindex="0"
            :aria-pressed="openCalendarPopover" :aria-label="t('search.togglecalendar')"
            @click="toggleCalendarPopover"
          >
            <CalendarDays :size="18" color="var(--yb-color-black)" aria-hidden="true" />
            <p :class="{ active: datesAsStrings }">
              {{ datesAsStrings ?? t('search.dates') }}
            </p>
          </button>
          <button
            v-if="datesAsStrings" type="button" class="delete-button" aria-label="Supprimer les dates"
            @click="deleteDates"
          >
            <Plus :size="18" color="var(--yb-color-black)" style="rotate:45deg;" />
          </button>
        </div>
        <div v-if="openCalendarPopover" ref="calendarPopover" class="popover">
          <TheCalendar :columns="2" :rows="1" />
        </div>
      </div>
      <div class="search-filter">
        <button type="button" class="search-action" @click="handleSearchInput(); openCalendarPopover = false">
          {{ t('search.button') }}
        </button>
      </div>
    </section>
    <div
      v-if="matchingRoutes.length > 0 && input.length >= 3 && isInputFocused"
      class="search-suggestions"
      @click.stop
    >
      <ul>
        <li
          v-for="route in matchingRoutes"
          :key="route.url"
          @mousedown.prevent="goToRoute(route.url)"
        >
          <div class="suggestion-item">
            <img
              v-if="route.thumbnail"
              :src="route.thumbnail"
              alt="aperçu de {{ route.title }}"
              class="suggestion-image"
              @error="route.thumbnail = 'https://empowher.org/wp-content/uploads/2021/03/image-placeholder-350x350-1-300x300.png'"
            >
            <span class="suggestion-title">
              {{ route.title }}
            </span>
          </div>
        </li>
      </ul>
    </div>
  </div>
  <TheMobileFilters v-else />
</template>

<style lang="css" scoped>
.search-container {
  position: relative;
  width: fit-content;
}

section {
  display: grid;
  grid-template-columns: repeat(4, auto);
  /* gap: var(--yb-spacing-xs); */
  place-items: center;
  width: fit-content;
  padding: var(--yb-spacing-xs);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-full);

  @media screen and (width < 1024px) {
    display: none;
  }

  .search-filter {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-inline: 2px;

    &:first-child {
      border-top-left-radius: var(--yb-radius-full);
      border-bottom-left-radius: var(--yb-radius-full);
    }

    &:last-child {
      border-top-right-radius: var(--yb-radius-full);
      border-bottom-right-radius: var(--yb-radius-full);
    }

    &:not(:nth-last-child(-n + 2)) {
      border-right: 1px solid var(--yb-color-mediumgray);
    }

    .popover {
      position: absolute;
      left: 0;
      width: fit-content;
      top: 3.25rem;
      background-color: var(--yb-color-white);
      border-radius: var(--yb-radius-sm);
      box-shadow: var(--yb-color-mediumgray) 0 4px 8px 0;
      z-index: 20;
    }

    .search-buttons {
      display: flex;
      align-items: center;
      /* gap: var(--yb-spacing-sm); */

      .delete-button {
        appearance: none;
        display: grid;
        place-items: center;
        padding: 5px;
        cursor: pointer;
        border: none;
        outline: none;
        background-color: transparent;
        border-radius: var(--yb-radius-sm);

        &:hover {
          /*background-color: var(--search-highlight-background-color);*/
          background-color: var(--yb-color-lightgray);
        }
      }
    }

    button.filter-wrapper {
      background-color: var(--yb-color-white);
      border: none;
      font-size: var(--yb-text-base);
      color: var(--yb-color-black);
      min-width: 180px;
    }

    .filter-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      width: fit-content;
      gap: var(--yb-spacing-xs);
      height: 44px;
      padding-left: var(--yb-spacing-sm);
      border-radius: var(--yb-radius-full);

      &.opened {
        /*background-color: var(--search-highlight-background-color);*/
        background-color: var(--yb-color-lightgray);
      }

      &:hover {
        /*background-color: var(--search-highlight-background-color);*/
        background-color: var(--yb-color-lightgray);
      }

      &:focus-visible {
        /*background-color: var(--search-highlight-background-color);*/
        background-color: var(--yb-color-lightgray);
        outline: none;
        border: none;
      }

      .search_query {
        max-width: 135px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: var(--yb-default-font, inherit);
      }

      .search_query,
      p.active {
        font-weight: var(--yb-text-bold);
      }

      .search_query::placeholder {
        font-weight: var(--yb-text-light);
      }

      input {
        background-color: transparent;
        outline: none;
        border: none;
        color: var(--yb-color-black);
        font-size: var(--yb-text-base);

        &::placeholder {
          color: var(--yb-color-black);
        }
      }

      p {
        margin-right: auto;
        max-width: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      p + * {
        margin-left: auto;
      }
    }

    .search-action {
      border-radius: var(--yb-radius-full);
      background-color: var(--search-action-background-color);
      color: var(--search-action-text-color);
      padding: 0.8rem var(--yb-spacing-xl);
      border: none;
      margin-left: 4px;
      font-size: var(--yb-text-base);
      font-weight: var(--yb-text-bold);
      cursor: pointer;

      &:hover {
        background-color: var(--yb-color-dark-primary);
        /* border: 2px solid var(--yb-color-dark-primary); */
      }
    }
  }
}

.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  width: 75%;
  max-height: calc((var(--yb-spacing-sm) * 2 + var(--yb-text-base) * 1.5) * 5.5);
  background-color: white;
  margin-top: var(--yb-spacing-xs);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-sm);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  overflow-y: auto;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    padding: var(--yb-spacing-sm) var(--yb-spacing-sm);
    cursor: pointer;
    font-size: var(--yb-text-base);
    transition: background-color 0.2s ease;
  }

  li:hover {
    background-color: var(--yb-color-lightgray);
  }

  .suggestion-item {
    display: flex;
    align-items: center;
    gap: var(--yb-spacing-sm);
  }

  .suggestion-image {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: var(--yb-radius-sm);
  }
}

/* clears the cross to clear the input from Internet Explorer */
input[type='search']::-ms-clear {
  display: none;
  width: 0;
  height: 0;
}

input[type='search']::-ms-reveal {
  display: none;
  width: 0;
  height: 0;
}

/* clears the cross to clear the input from Chrome */
input[type='search']::-webkit-search-decoration,
input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-results-button,
input[type='search']::-webkit-search-results-decoration {
  display: none;
}
</style>
